<template>
  <div class="PromoTracks">
    <div v-if="tracks.length" class="flex justify-between items-center px-gr-6 py-gr-half-5">
      <!-- Track Count -->
      <div
        class="font-bolder text-xs text-gray-500 flex flex-col items-start space-y-gr-2 sm:space-y-0 sm:space-x-gr-5 sm:flex-row"
      >
        <span class="uppercase">{{ playableTracks.length }} of {{ tracks.length }} tracks</span>
        <span class="" v-if="runtime">
          {{ formattedRuntime }}
        </span>
      </div>
      <div
        class="flex flex-col items-end space-y-gr-2 sm:space-y-0 sm:space-x-gr-4 sm:flex-row"
        :class="{
          'opacity-50 pointer-events-none': !playableTracks.length,
        }"
      >
        <!-- Preload Button -->
        <button
          v-if="showPreloadButton"
          @click.prevent="preloadAll = !preloadAll"
          class="opacity-0 pl-gr-2 pr-gr-1 -mr-gr-2 uppercase font-bolder text-xs text-gray-500 inline-flex items-center focus:ring focus:outline-none"
        >
          <span v-if="hasLoadingErrors"> Errored </span>
          <template v-else>
            <span class="mr-gr-4">
              {{ preloadAll && !allTracksPreloaded ? 'Downloading' : 'Listen Offline' }}
            </span>
            <span :class="['text-2xl opacity-75', { hidden: preloadAll }]"
              ><i class="fad fa-fw fa-toggle-off"></i
            ></span>
            <span
              :class="['text-2xl text-tertiary-500', { hidden: allTracksPreloaded || !preloadAll }]"
            >
              <i class="fad fa-fw fa-spinner-third fa-spin scale-[0.8]"></i>
            </span>
            <span
              :class="[
                'text-2xl text-tertiary-500',
                { hidden: !allTracksPreloaded || !preloadAll },
              ]"
              ><i class="fad fa-fw fa-toggle-on"></i
            ></span>
          </template>
        </button>

        <!-- Autoplay -->
        <button
          @click.prevent="toggleAudioAutoplay"
          class="pl-gr-2 pr-gr-1 -mr-gr-2 uppercase font-bolder text-xs text-gray-500 inline-flex items-center focus:ring focus:outline-none"
        >
          <span class="mr-gr-4">Autoplay</span>
          <span :class="['text-2xl text-tertiary-500', { hidden: !audioAutoplay }]"
            ><i class="fad fa-fw fa-toggle-on"></i
          ></span>
          <span :class="['text-2xl opacity-75', { hidden: audioAutoplay }]"
            ><i class="fad fa-fw fa-toggle-off"></i
          ></span>
        </button>
      </div>
    </div>

    <div v-else class="text-center mt-gr-8">
      <div class="text-5xl fa fa-pause-circle"></div>
      <div class="mt-gr-4 text-3xl leading-gr-7">A quick pause.</div>
      <p class="mt-gr-4">No tracks are uploaded for this audiobook yet!</p>
    </div>

    <!-- Playable Tracks -->
    <div v-if="playableTracks.length" class="space-y-gr-4">
      <div
        v-for="(track, index) in playableTracks"
        :key="track.id"
        :class="[
          'flex items-center p-gr-4 pr-gr-5 bg-white/75 rounded hover:bg-white cursor-pointer dark:bg-primary-darkest/75 dark:hover:bg-bg-primary-darkest',
          {
            'shadow-lg': index === activeAudioIndex,
            'text-primary-500/50 hover:text-primary-800/80 dark:hover:text-primary-400':
              index !== activeAudioIndex,
          },
        ]"
        @click.prevent="onTrackClick(index)"
      >
        <div class="mr-gr-half-5">
          <button
            ref="playPauseButtons"
            class="relative h-gr-6 w-gr-6 box-content p-gr-half-5 rounded inline-flex items-center justify-center text-xs"
            @click.stop="onTrackClick(index)"
          >
            <span :class="[showPlayIcon(index) ? '' : 'hidden']">
              <i class="fa fa-fw fa-sm fa-play"></i>
            </span>
            <span :class="[showPauseIcon(index) ? '' : 'hidden']">
              <i class="fa fa-fw fa-sm fa-pause"></i>
            </span>
            <span :class="[showLoadingIcon(index) ? '' : 'hidden']">
              <i class="fad fa-fw fa-lg fa-spinner-third fa-spin"></i>
            </span>
            <span :class="[showErrorIcon(index) ? '' : 'hidden']" :title="getTrackError(index)">
              <i class="fad fa-fw fa-lg fa-exclamation-circle text-red-500"></i>
            </span>
            <span :class="[showPlayCheckIcon(index) ? '' : 'hidden']">
              <i
                class="fa fa-fw fa-xs fa-check text-tertiary-500 absolute left-50 top-50 mt-gr-3 -ml-gr-2"
              ></i>
            </span>
          </button>
        </div>
        <div class="flex-grow truncate">
          <span class="font-bolder mr-gr-4">{{ track.name }}</span>
          <span v-if="track.title">{{ track.title }}</span>
          <div>
            <audio
              class="hidden"
              :ref="(el) => addAudioElement(el, index)"
              controls
              :preload="getAudioPreload(index)"
              :title="track.name"
              :src="track.track"
            ></audio>
          </div>
          <div
            @click.stop="(event) => audioSeek(event, index)"
            :class="[
              'mt-gr-2 h-gr-3 relative duration-200',
              { 'opacity-25': !isTrackActive(index), 'pointer-events-none': isMobile },
            ]"
          >
            <div
              class="pointer-events-none absolute inset-y-0 left-0 bg-gray-50 dark:bg-gray-900/50 w-full"
            ></div>
            <div
              class="pointer-events-none absolute inset-y-0 left-0 duration-250 bg-gray-200/75 dark:bg-gray-800/75"
              :style="{ width: getTrackProgressWidth(index) }"
            ></div>
            <div
              :class="[
                'pointer-events-none absolute inset-y-0 left-0 duration-250 bg-tertiary-400 dark:bg-tertiary-600',
                { 'ease-linear': isTrackPlaying(index) },
                {
                  'bg-diag-bars':
                    !isSafari && !isIOS && !isTrackReady(index) && isTrackActive(index),
                },
              ]"
              :style="{ width: getTrackPositionWidth(index) }"
            ></div>
          </div>
        </div>
        <div v-if="showDownloadBtn" class="shrink -mr-gr-5">
          <a
            @click.prevent.stop="downloadTrack(track)"
            :href="track.track"
            target="_blank"
            rel="noopener"
            class="link p-gr-5 relative"
            download
            :title="`${track.name} - ${track.title}`"
          >
            <span :hidden="!isDownloading(track)"
              ><i class="fa-fw fad fa-spinner-third fa-spin"></i
            ></span>
            <span :hidden="isDownloading(track) || !hasDownloadError(track)"
              ><i class="fa-fw fad fa-exclamation-circle text-danger-500"></i
            ></span>
            <span :hidden="isDownloading(track) || hasDownloadError(track) || !hasDownloaded(track)"
              ><i class="fa-fw fas fa-check text-success-500"></i
            ></span>
            <span :hidden="isDownloading(track) || hasDownloadError(track) || hasDownloaded(track)"
              ><i class="fa-fw far fa-arrow-to-bottom"></i
            ></span>
          </a>
        </div>
      </div>
    </div>

    <!-- Unplayable Tracks -->
    <div v-if="unplayableTracks.length" class="relative space-y-gr-4">
      <div
        v-for="track in unplayableTracks"
        :key="track.id"
        class="opacity-30 pointer-events-none flex items-center p-gr-4 pr-gr-5 bg-white/75 rounded dark:bg-primary-darkest/75 text-gray-500 dark:text-gray-400"
      >
        <div class="mr-gr-half-5">
          <button
            class="relative h-gr-6 w-gr-6 box-content p-gr-half-5 rounded inline-flex items-center justify-center text-xs"
          >
            <i class="fa fa-fw fa-lock"></i>
          </button>
        </div>
        <div class="flex-grow truncate">
          <span class="font-bolder mr-gr-2">{{ track.name }}</span>
          <span v-if="track.title">{{ track.title }}</span>
        </div>
      </div>
      <subdomain-link
        v-if="showAccessBtn"
        :href="accessLink"
        :class="[
          'button text-lg rounded-full bg-primary-700 text-white hover:bg-primary-600 absolute z-10 left-1/2 -translate-x-1/2',
          unplayableTracks.length >= 3 ? 'top-gr-9' : 'top-gr-half-8',
        ]"
      >
        <i class="fad fa-fw fa-lg fa-headphones-alt mr-gr-3"></i>Get Access
      </subdomain-link>
    </div>
  </div>
</template>

<script>
  import { AudioBus } from '@/Utils/bus';
  import axios from 'axios';
  import audio from '@/Mixins/audio';
  import { isMobile, isIOS, isSafari } from '@/Utils/device';

  export default {
    name: 'PromoTracks',

    mixins: [audio],

    props: ['tracks', 'promo', 'book', 'team', 'showAccessBtn', 'showDownloadBtn', 'accessLink'],

    data() {
      return {
        autoplay: true,
        isMobile: isMobile(),
        start_seconds: null,
        end_seconds: null,
        audioElements: [],
        trackPlaysByIndex: {},
        showPreloadButton: false,
        preloadAll: false,
        isOnline: true,
        allTracksCached: false,
        cachedTracks: [],
        trackPositionInterval: 0,
        windowBlurWasPlaying: true,
        downloadingTracks: [],
        downloadedTracks: [],
        downloadedErrors: [],
        countedDownloads: false,
        isIOS,
        isSafari,
      };
    },

    computed: {
      allTracksPreloaded() {
        return this.cachedTracks.length === this.playableTracks.length;
        // let totalProgress = Object.values(this.audioProgress).reduce((a, b) => a + b, 0);
        // return totalProgress === this.audioElements.length * 100;
      },

      user() {
        return this.$page.props.user;
      },

      playableTracks() {
        return this.tracks.filter((t) => t.track);
      },

      unplayableTracks() {
        return this.tracks.filter((t) => !t.track);
      },

      currentTrack() {
        return this.playableTracks[this.activeAudioIndex] || null;
      },

      trackIndexById() {
        return (
          this.playableTracks?.reduce((obj, cur, index) => {
            obj[cur.id] = index;
            return obj;
          }, {}) || {}
        );
      },

      runtime() {
        if (this.book?.runtime) return this.book.runtime;
        let hasAll = true;
        let total = this.playableTracks.reduce((sec, track) => {
          if (!track.duration) hasAll = false;
          return sec + track.duration;
        }, 0);
        return hasAll ? total : null;
      },

      formattedRuntime() {
        let seconds = this.runtime;

        let runtimeH = Math.floor(seconds / 60 / 60);
        let runtimeM = Math.floor(seconds / 60 - runtimeH * 60);
        let runtimeS = Math.floor(seconds - runtimeH * 60 * 60 - runtimeM * 60);

        let label = '';
        if (runtimeH > 0) {
          label += `${runtimeH}${runtimeH !== 1 ? 'h' : 'h'}`;
        }
        if (runtimeM > 0) {
          label += ` ${runtimeM}${runtimeM !== 1 ? 'm' : 'm'}`;
        }

        return label;
      },
    },

    watch: {
      downloadedTracks(val) {
        if (val?.length === this.playableTracks.length && !this.countedDownloads) {
          axios.post(route('download.update', { promo: this.promo }));
          this.countedDownloads = true;
        }
      },

      preloadAll(val) {
        if (val) {
          if (!this.allTracksPreloaded) this.forceLoadAllAudio();
        }
      },

      isOnline(val) {
        if (!val) {
          window.onbeforeunload = this.onBeforeUnload;
        } else {
          window.onbeforeunload = () => {};
        }
      },

      currentTrack(val, oldVal) {
        this.setMediaMetadata(val);
      },
    },

    created() {
      // if ('storage' in navigator && 'estimate' in navigator.storage) {
      //   navigator.storage
      //     .estimate()
      //     .then((estimate) => {
      //       console.log(`Using ${estimate.usage} out of ${estimate.quota} bytes.`);
      //       this.showPreloadButton = true;
      //     })
      //     .catch(() => {});
      // }
    },

    mounted() {
      this.getTrackPlays();
      this.setMediaMetadata();

      this.isOnline = window.navigator?.onLine;
      window.addEventListener('online', () => {
        this.isOnline = true;
      });
      window.addEventListener('offline', () => {
        this.isOnline = false;
      });
      window.addEventListener('blur', this.onWindowBlur);
      window.addEventListener('focus', this.onWindowFocus);

      AudioBus.$on('play', this.onTrackChange);
      AudioBus.$on('pause', this.onTrackChange);
      AudioBus.$on('ended', this.onTrackChange);
      AudioBus.$on('timeupdate', this.onTrackChange);
      AudioBus.$on('seekAndPlay', this.onTrackChange);
      AudioBus.$on('loadeddata', this.onTrackChange);
      AudioBus.$on('loadedmetadata', this.onTrackChange);
      window.addEventListener('beforeunload', () => {
        this.onTrackChange({
          index: this.currentTrackIndex,
          audio: this.audioElements[this.currentTrackIndex],
          event: 'unload',
        });
      });

      // let promises = [];
      // this.playableTracks.forEach((track) => {
      //   let url = route('listen.promo.track.alias', {
      //     team: this.team.subdomain,
      //     promo: this.promo,
      //     alias: track.alias,
      //   });
      //   promises.push(this.checkCacheTrack(url));
      // });
      // Promise.all(promises).then((results) => {
      //   if (results.every((result) => !!result)) {
      //     this.forceLoadAllAudio();
      //     this.preloadAll = true;
      //   }
      // });
    },

    beforeUnmount() {
      window.removeEventListener('online', () => {}, false);
      window.removeEventListener('offline', () => {}, false);
      window.removeEventListener('blur', this.onWindowBlur, false);
      window.removeEventListener('focus', this.onWindowFocus, false);
      window.onbeforeunload = () => {};
      window.removeEventListener('beforeunload', () => {}, false);
      AudioBus.$off('play', this.onTrackChange);
      AudioBus.$off('pause', this.onTrackChange);
      AudioBus.$off('ended', this.onTrackChange);
      AudioBus.$off('timeupdate', this.onTrackChange);
      AudioBus.$off('seekAndPlay', this.onTrackChange);
      AudioBus.$off('loadeddata', this.onTrackChange);
      AudioBus.$off('loadedmetadata', this.onTrackChange);
    },

    methods: {
      setMediaMetadata(currentTrack = null) {
        if (currentTrack === null) currentTrack = this.currentTrack;
        if (currentTrack && 'mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title:
              currentTrack.name +
              (currentTrack.title ? ` - ${currentTrack.title}` : '') +
              ` - ${this.book.title}`,
            artist: this.book.author_list,
            album: this.book.title,
            artwork: [
              {
                src: `${this.book.cover}?w=1024&h=1024&fm=png`,
                sizes: '1024x1024',
                type: 'image/png',
              },
            ],
          });
        }
      },

      toggleAudioAutoplay(ev, force = null) {
        this.audioAutoplay = force !== null ? force : !this.audioAutoplay;
      },

      async downloadTrack(track) {
        // Show loading icon
        this.downloadingTracks = [...new Set([...this.downloadingTracks, track.id])];

        // Prepare and fetch track
        let url = track.track;
        let isBlob = false;
        await fetch(track.track)
          .then((resp) => resp.blob())
          .then((blob) => {
            isBlob = true;
            url = window.URL.createObjectURL(blob);
          })
          .catch((err) => {
            isBlob = false;
            url = track.track;
            // this.downloadedErrors = [...new Set([...this.downloadedErrors, track.id])];
            // alert(`An error occurred. Please, try again!\n\n${err}`);
          });

        // Create and click a download link
        const link = document.createElement('a');
        link.style.display = 'none';
        link.target = '_blank';
        link.href = url;
        link.download = `${this.book.title} - ${track.filename}`;
        document.body.appendChild(link);
        link.click();
        if (isBlob) window.URL.revokeObjectURL(url);

        // Add to downloaded
        this.downloadedTracks = [...new Set([...this.downloadedTracks, track.id])];

        // Hide loading icon
        this.$nextTick(() => {
          let index = this.downloadingTracks.indexOf(track.id);
          if (index !== -1) {
            delete this.downloadingTracks[index];
          }
        });
      },

      isDownloading(track) {
        return this.downloadingTracks.indexOf(track.id) !== -1;
      },

      hasDownloadError(track) {
        return this.downloadedErrors.indexOf(track.id) !== -1;
      },

      hasDownloaded(track) {
        return this.downloadedTracks.indexOf(track.id) !== -1;
      },

      getAudioPreload(index) {
        return this.preloadAll || this.audioToPreload[index] ? 'auto' : 'none';
      },

      forceLoadAllAudio() {
        this.audioElements.forEach((audio, index) => {
          this.audioLoading[index] = true;
          let track = this.playableTracks[index];

          this.audioLoading[index] = false;
          var link = document.createElement('link');
          link.rel = 'preload';
          link.href = audio.src;
          link.as = 'audio';
          link.onload = () => {
            // Done loading the mp3
            this.cachedTracks.push(index);
          };
          document.head.appendChild(link);
          // let lastTime = audio.currentTime;
          // let newAudioSrc = route('listen.promo.track.alias', {
          //   team: this.team.subdomain,
          //   promo: this.promo,
          //   alias: track.alias,
          // });
          // audio.src = newAudioSrc;
          // audio.currentTime = lastTime;
          // if (!this.allTracksPreloaded) this.checkCacheTrack(newAudioSrc);
        });

        // But they may not be fully loaded yet.
        this.allTracksCached = true;
      },

      checkCacheTrack(url) {
        return new Promise((res, rej) => {
          if (this.allTracksPreloaded) return res(true);
          if (this.cachedTracks.indexOf(url) !== -1) return res(true);

          // Check cache until we know it's there
          let checkInterval = setInterval(() => {
            caches.match(url).then((response) => {
              if (response && this.cachedTracks.indexOf(url) === -1) {
                this.cachedTracks.push(url);
                clearInterval(checkInterval);
                return res(true);
              }
            });
          }, 1000);

          // Stop checking after 1 minute
          setTimeout(() => {
            clearInterval(checkInterval);
            return rej(new Error('Timeout'));
          }, 60 * 1000);
        });
      },

      onBeforeUnload(event) {
        event.preventDefault();
        return 'You have already pre-loaded tracks. Are you sure you want to refresh (and have to download them again)?';
      },

      addAudioElement(el, index) {
        if (this.audioElements.length - 1 <= index && this.audioElements[index] !== el) {
          this.audioElements[index] = el;
        }
      },

      showPlayIcon(index) {
        return (
          !this.showErrorIcon(index) && !this.showLoadingIcon(index) && !this.isTrackPlaying(index)
        );
      },

      showPauseIcon(index) {
        return (
          !this.showErrorIcon(index) && !this.showLoadingIcon(index) && this.isTrackPlaying(index)
        );
      },

      showLoadingIcon(index) {
        return (
          this.isTrackActive(index) && !this.showErrorIcon(index) && this.isTrackLoading(index)
        );
      },

      showPlayCheckIcon(index) {
        return this.isTrackEnded(index);
      },

      showErrorIcon(index) {
        return this.isTrackErrored(index);
      },

      onWindowBlur() {
        this.trackPositionInterval = setInterval(() => {
          let index = this.activeAudioIndex;
          let audio = this.audioElements[index];
          if (audio) {
            let isPlaying = !audio.paused && audio.currentTime > 0 && !audio.ended;
            let event =
              isPlaying && !this.windowBlurWasPlaying
                ? 'play'
                : !isPlaying && this.windowBlurWasPlaying
                ? 'pause'
                : isPlaying && this.windowBlurWasPlaying
                ? 'seeking'
                : 'pause';
            this.onTrackChange({ index, audio, event });
            this.windowBlurWasPlaying = isPlaying;
          }
        }, 5000);
      },

      onWindowFocus() {
        this.windowBlurWasPlaying = true;
        clearInterval(this.trackPositionInterval);
      },

      onTrackChange({ index, audio, event, lastTime = null }) {
        const trackPlay = () => {
          if (!this.user?.id) return;
          if (this.start_seconds == null) return;
          if (this.end_seconds == null) return;
          if (!this.currentTrack?.id) return;
          if (Math.round(this.end_seconds) <= Math.round(this.start_seconds)) return;
          return axios
            .post(route('listen.promo.track.play', route().params), {
              track_id: this.currentTrack?.id,
              start_seconds: this.start_seconds || 0,
              end_seconds: this.end_seconds,
              completed: event === 'ended',
              player_config: { playbackRate: audio.playbackRate },
            })
            .catch((err) => {
              console.log('%c err -->', 'color:#F80', err);
            })
            .finally(() => {
              this.start_seconds = audio.currentTime;
              this.end_seconds = null;
            });
        };

        if (event == 'ended' || event == 'loadedmetadata') {
          let track = this.playableTracks[index];
          this.saveTrackDuration(track, audio, event == 'ended');
        }

        switch (event) {
          case 'play':
            this.start_seconds = audio.currentTime;
            break;
          case 'loadeddata':
            this.setAudioPosition(index);
            break;
          case 'timeupdate':
            this.end_seconds = audio.currentTime;
            break;
          case 'seekAndPlay':
          case 'seeking':
            trackPlay();
            this.start_seconds = audio.currentTime;
            this.end_seconds = lastTime;
          case 'pause':
            if (audio.currentTime === audio.duration) break;
          case 'seeking':
          case 'pause':
          case 'seekAndPlay':
          case 'ended':
          case 'unload':
            trackPlay();
            break;
        }
      },

      getTrackPlays() {
        if (this.user?.id) {
          axios.get(route('listen.promo.track.plays', route().params)).then(({ data }) => {
            let trackPlays = data.trackPlays;

            trackPlays.forEach(({ track_id, end_seconds, completed, player_config }, i) => {
              let index;
              if (this.trackIndexById?.hasOwnProperty(track_id)) {
                index = this.trackIndexById[track_id];
              }
              if (index === undefined) return;
              this.trackPlaysByIndex[index] = { track_id, end_seconds, completed, player_config };

              // Mark completed, or not
              this.audioEnded[index] = completed;

              // Set last played position, or not
              this.setAudioPosition(index, end_seconds);

              // Show loading for last played track
              if (i === 0) {
                this.activeAudioIndex = index;
                this.audioToPreload[index] = true;
                AudioBus.$emit('activeAudioIndex', index);
                if (player_config?.playbackRate) {
                  AudioBus.$emit('onPlaybackRate', {
                    playbackRate: player_config.playbackRate,
                    increase: false,
                  });
                }
              }
            });
          });
        } else {
          // If no track plays, the first track will be the active one
          this.activeAudioIndex = 0;
          this.audioToPreload[0] = true;
          AudioBus.$emit('activeAudioIndex', 0);
        }
      },

      setAudioPosition(index, end_seconds = null) {
        let audio = this.audioElements[index];
        if (!audio) return;
        if (end_seconds === null) {
          end_seconds = this.trackPlaysByIndex[index]?.end_seconds || 0;
        }
        audio.currentTime = end_seconds;
        let track = this.playableTracks[index];
        if (track?.duration) {
          let position = (end_seconds / track.duration) * 100;
          this.audioPosition[index] = position;
        }
      },

      saveTrackDuration(track, audio, force = false) {
        if (force || (audio?.duration && track?.id && !track?.duration)) {
          axios.post(route('track.duration', [track.id]), {
            duration: Math.max(audio.duration, audio.currentTime),
          });
        }
      },
    },
  };
</script>

<!-- <style lang="scss" src="./PromoTracks.scss" scoped></style> -->
